'use client';

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowsOut, X, DownloadSimple, WarningCircle, CheckCircle } from '@phosphor-icons/react';

interface BatchItemInput {
  label: string;
  prompt: string;
}

interface BatchItemResult {
  label: string;
  prompt: string;
  success: boolean;
  url?: string;
  allImages?: string[];
  error?: string;
}

interface GeneratedImageBatchOutput {
  success: boolean;
  results?: BatchItemResult[];
  error?: string;
}

function useElapsedSeconds(startKey: string | number): number {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    setSecs(0);
    const t0 = Date.now();
    const id = setInterval(() => setSecs(Math.floor((Date.now() - t0) / 1000)), 250);
    return () => clearInterval(id);
  }, [startKey]);
  return secs;
}

/**
 * Renders the parallel image-batch tool's output.
 *
 * - **Pending**: show N skeleton cards in a grid. No labels, no prompts,
 *   no model names, no costs — just "generation is happening" with shimmer
 *   + a tiny elapsed-seconds timer. Internals stay internal.
 * - **Done**: show each image (or amber error tile for per-item failures).
 *   Clean — no overlay text, just the image. Hover for download + fullscreen.
 *
 * AI SDK v6 doesn't expose per-item streaming yet, so the swap is
 * all-at-once when the tool resolves. The skeleton phase is the win.
 */
export function GeneratedImageBatchToolOutput({
  input,
  output,
}: {
  input?: { items?: BatchItemInput[] };
  output?: GeneratedImageBatchOutput;
}) {
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);

  const items = input?.items ?? [];
  const results = output?.results ?? [];
  const isPending = !output;
  // During input-streaming the items count may not be fully known. Fall
  // back to a guess of 4 so the layout doesn't pop. When done, use the
  // real count from results.
  const n = isPending ? Math.max(items.length, 1) : results.length;
  const cols = n === 1 ? 1 : n === 2 ? 2 : n <= 4 ? 2 : 3;
  const elapsed = useElapsedSeconds(`${n}-${isPending ? 'p' : 'd'}`);

  if (!isPending && !output?.success && !results.length) {
    return (
      <div className="p-3 border border-amber-500/50 rounded-lg bg-amber-500/10">
        <p className="text-sm font-medium text-amber-600 dark:text-amber-400">Image generation failed</p>
        <pre className="mt-1 text-xs text-muted-foreground whitespace-pre-wrap">{output?.error}</pre>
      </div>
    );
  }

  if (!isPending && n === 0) {
    return (
      <div className="p-3 border border-muted rounded-lg bg-muted/50">
        <p className="text-sm text-muted-foreground">No images.</p>
      </div>
    );
  }

  return (
    <>
      <div className="w-full border rounded-lg overflow-hidden bg-card">
        <div className="flex items-center justify-between px-3 py-1.5 bg-muted/50 border-b gap-3">
          <div className="flex items-center gap-2 min-w-0">
            {isPending ? (
              <motion.span
                aria-hidden
                className="inline-block size-2 rounded-full bg-violet-500"
                animate={{ opacity: [0.4, 1, 0.4], scale: [1, 1.3, 1] }}
                transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
              />
            ) : output?.success ? (
              <CheckCircle weight="fill" className="size-3.5 shrink-0 text-emerald-500" />
            ) : null}
            <span className="text-xs text-muted-foreground truncate">
              {isPending ? 'Generating images…' : 'Done'}
            </span>
          </div>
          {isPending && elapsed > 0 && (
            <span className="text-[10px] font-mono tabular-nums text-muted-foreground/70">{elapsed}s</span>
          )}
        </div>

        <div
          className="p-2 grid gap-2"
          style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
        >
          {Array.from({ length: n }).map((_, idx) => {
            const result = isPending ? undefined : results[idx];

            if (result?.success && result.url) {
              return (
                <div
                  key={idx}
                  className="relative group rounded overflow-hidden bg-muted/20 cursor-pointer"
                  onClick={() => setFullscreenImage(result.url!)}
                >
                  <img
                    src={result.url}
                    alt="Generated image"
                    className="w-full h-auto object-contain"
                    style={{ maxHeight: cols === 1 ? 500 : cols === 2 ? 320 : 240 }}
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <button
                      className="p-2 rounded-full bg-background/90 backdrop-blur-sm border shadow-sm hover:bg-background transition-colors"
                      title="View fullscreen"
                    >
                      <ArrowsOut weight="bold" className="size-5" />
                    </button>
                  </div>
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <a
                      href={result.url}
                      download={`image-${idx + 1}.png`}
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center justify-center size-7 rounded-full bg-background/90 backdrop-blur-sm border shadow-sm hover:bg-background transition-colors"
                      title="Download"
                    >
                      <DownloadSimple weight="bold" className="size-3.5" />
                    </a>
                  </div>
                </div>
              );
            }

            if (result && !result.success) {
              return (
                <div
                  key={idx}
                  className="relative rounded overflow-hidden border border-amber-500/40 bg-amber-500/5 p-3 flex flex-col items-center justify-center gap-1.5 text-amber-600 dark:text-amber-400"
                  style={{ minHeight: cols === 1 ? 200 : 160 }}
                >
                  <WarningCircle weight="bold" className="size-5" />
                  <span className="text-[11px]">Failed</span>
                </div>
              );
            }

            return <SkeletonCard key={idx} aspect={cols === 1 ? 'tall' : 'short'} index={idx} />;
          })}
        </div>
      </div>

      <AnimatePresence>
        {fullscreenImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setFullscreenImage(null)}
          >
            <button
              className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              onClick={() => setFullscreenImage(null)}
            >
              <X weight="bold" className="size-6" />
            </button>
            <a
              href={fullscreenImage}
              download="generated-image.png"
              onClick={(e) => e.stopPropagation()}
              className="absolute top-4 right-16 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              title="Download"
            >
              <DownloadSimple weight="bold" className="size-6" />
            </a>
            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              src={fullscreenImage}
              alt="Generated image"
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function SkeletonCard({
  aspect,
  index = 0,
}: {
  aspect: 'tall' | 'short';
  index?: number;
}) {
  const height = aspect === 'tall' ? 500 : 240;
  // Stagger the pulse phase per card so the grid feels alive, not synced.
  const delay = (index % 4) * 0.18;
  return (
    <div
      className="relative rounded overflow-hidden bg-gradient-to-br from-violet-500/10 via-fuchsia-500/[0.07] to-violet-500/5 border border-violet-500/15"
      style={{ minHeight: height, maxHeight: height }}
    >
      <motion.div
        aria-hidden
        className="absolute inset-y-0 -inset-x-1/2 pointer-events-none"
        style={{
          background:
            'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.06) 50%, transparent 70%)',
        }}
        animate={{ x: ['-50%', '50%'] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: 'linear', delay }}
      />
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div
          className="size-10 rounded-full bg-violet-500/15 border border-violet-500/30"
          animate={{ scale: [1, 1.15, 1], opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut', delay }}
        />
      </div>
    </div>
  );
}
